/**
 * HTTP client for the KH AI Academy API.
 *
 * Design notes
 * ------------
 * * The access token lives in memory only. It is never written to
 *   `localStorage`, so an XSS cannot read it from storage.
 * * The refresh token is an HttpOnly cookie the browser handles on its own;
 *   JavaScript never sees it.
 * * On a 401, the client silently refreshes once and replays the request.
 *   Concurrent 401s share a single refresh promise.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
    readonly fieldErrors?: { field: string; message: string }[],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
}

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';

let accessToken: string | null = null;
let refreshPromise: Promise<boolean> | null = null;
const sessionLostListeners = new Set<() => void>();

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * Renews the session, sharing one in-flight request.
 *
 * Exposed so that the application's bootstrap uses the same de-duplication as
 * the automatic retry on a 401. Refresh tokens are single-use: sending two
 * renewals with the same cookie is what a stolen token looks like, and the API
 * (rightly) reacts by revoking every session.
 */
export function renewSession(): Promise<boolean> {
  return refreshSession();
}

export function onSessionLost(listener: () => void): () => void {
  sessionLostListeners.add(listener);
  return () => sessionLostListeners.delete(listener);
}

function notifySessionLost(): void {
  accessToken = null;
  sessionLostListeners.forEach((listener) => listener());
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Set for the auth endpoints themselves, to avoid a refresh loop. */
  skipRefresh?: boolean;
  query?: Record<string, string | number | boolean | undefined | null>;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE_URL}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function parseError(response: Response): Promise<ApiError> {
  let code = `http_${response.status}`;
  let detail = response.statusText || 'Erreur inattendue.';
  let requestId: string | undefined;
  let fieldErrors: { field: string; message: string }[] | undefined;
  try {
    const body = await response.json();
    if (typeof body?.detail === 'string') detail = body.detail;
    if (typeof body?.code === 'string') code = body.code;
    if (typeof body?.request_id === 'string') requestId = body.request_id;
    if (Array.isArray(body?.errors)) fieldErrors = body.errors;
  } catch {
    // A non-JSON body (gateway error page) keeps the generic message.
  }
  return new ApiError(response.status, code, detail, requestId, fieldErrors);
}

async function refreshSession(): Promise<boolean> {
  // A single in-flight refresh is shared by every concurrent 401.
  refreshPromise ??= (async () => {
    try {
      const response = await fetch(buildUrl('/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return false;
      const data = (await response.json()) as { access_token?: string };
      if (!data.access_token) return false;
      accessToken = data.access_token;
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

async function send(path: string, options: RequestOptions, retry: boolean): Promise<Response> {
  const { body, query, headers, skipRefresh, ...rest } = options;
  void skipRefresh; // consumed below via `options.skipRefresh`, not forwarded to fetch
  const requestHeaders = new Headers(headers);
  requestHeaders.set('Accept', 'application/json');
  if (body !== undefined) requestHeaders.set('Content-Type', 'application/json');
  if (accessToken) requestHeaders.set('Authorization', `Bearer ${accessToken}`);

  const response = await fetch(buildUrl(path, query), {
    ...rest,
    headers: requestHeaders,
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 401 && retry && !options.skipRefresh) {
    const refreshed = await refreshSession();
    if (refreshed) return send(path, options, false);
    notifySessionLost();
  }
  return response;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options, true);
  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'GET', query }),
  post: <T>(path: string, body?: unknown, options: RequestOptions = {}) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

/** Absolute URL of an authenticated download (resolved by the browser). */
export function downloadUrl(resourceId: string): string {
  return buildUrl(`/me/resources/${resourceId}/download`);
}
